import { Badge, Popover, PopoverAnchor } from "@au-schein-de/au-ui-library";
import type { ProductNoEdibleInfo } from "@dransay/api";
import { CATEGORIES, isDeviceKind } from "@dransay/api/models";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { HeartIcon } from "~/components/icons/heart-icon";
import { useFavorites } from "~/hooks/use-favorites.hook";
import { useIsDesktop } from "~/hooks/use-is-desktop.hook";
import { useMarkFavorites } from "~/hooks/use-mark-favorites.hook";
import { getThcCbdUnit } from "~/utils/product-unit.utils";
import type { ExpertReview } from "./expert-review/expert-review.types";
import { ProductDetailsCTAExpertReview } from "./product-details-cta-expert-review.component";
import { ProductDetailsCTAUserRating } from "./product-details-cta-user-rating.component";
import {
	type RatingBreakdownEntry,
	RatingBreakdownPopoverPanel,
} from "./rating-breakdown-popover.component";

type ProductDetailsSummaryProps = {
	product: ProductNoEdibleInfo;
	showExpertReview: boolean;
	expertReview?: ExpertReview;
	onExpertReviewClick?: () => void;
	ratingBreakdown?: ReadonlyArray<RatingBreakdownEntry>;
};

export function ProductDetailsSummary({
	product,
	showExpertReview,
	expertReview,
	onExpertReviewClick,
	ratingBreakdown,
}: ProductDetailsSummaryProps) {
	const { genetics, thc, cbd, name, kind, rating, ratingCount, category } =
		product;
	const { t } = useTranslation();
	const { toggleFavorite } = useFavorites();
	const { isFavorite: checkIsFavorite } = useMarkFavorites();
	const isFavorite = checkIsFavorite(product.id);
	const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
	const isDesktop = useIsDesktop();

	const handleBreakdownOpenChange = useCallback((open: boolean) => {
		setIsBreakdownOpen(open);
	}, []);

	const isDevice =
		category === CATEGORIES.INHALATIONS_EXTRACT && isDeviceKind(kind);
	const hasGenetics = !isDevice && Boolean(genetics);
	const hasThcBadge = !isDevice && thc != null && thc > 0;
	const hasCbdBadge = !isDevice && cbd != null && cbd > 0;

	const lineage = undefined;

	const rawRating = Number(rating);
	const ratingFloat = Number.isFinite(rawRating) ? rawRating : 0;
	const ratingClamped = Math.min(Math.max(ratingFloat, 0), 5);
	const hasBreakdown =
		ratingBreakdown !== undefined && ratingBreakdown.length > 0;

	const content = (
		<div className="flex flex-col gap-3">
			<div className="flex gap-1">
				{hasGenetics && <Badge className="capitalize">{genetics}</Badge>}
				{hasThcBadge && (
					<Badge>
						{t("THC")} {thc}
						{getThcCbdUnit(category)}
					</Badge>
				)}
				{hasCbdBadge && (
					<Badge>
						{t("CBD")} {cbd}
						{getThcCbdUnit(category)}
					</Badge>
				)}
			</div>

			<div className="">
				<div className="flex justify-between w-full items-center gap-4">
					<h1 className="text-h4 md:text-h3 text-gray-700">{name}</h1>

					<button
						type="button"
						onClick={() => toggleFavorite(product.id, product.name)}
						className="p-3 rounded-full bg-gray-100 flex-shrink-0 group cursor-pointer"
					>
						<HeartIcon
							filled={isFavorite}
							className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors"
						/>
					</button>
				</div>

				<div className="flex flex-col md:flex-row gap-1 md:gap-0">
					<p className="text-body-lg text-gray-700">{kind}</p>

					{lineage && (
						<div className="md:before:content-['|'] md:before:px-1">
							{lineage}
						</div>
					)}
				</div>
			</div>

			<div
				className={`flex flex-row gap-10 md:gap-6 mt-2 ${
					showExpertReview && expertReview
						? "justify-center md:justify-start items-center"
						: "justify-start"
				}`}
			>
				{showExpertReview && expertReview && (
					<ProductDetailsCTAExpertReview
						expertReview={expertReview}
						onClick={onExpertReviewClick}
					/>
				)}
				<ProductDetailsCTAUserRating
					rating={rating}
					ratingCount={ratingCount}
					horizontal={!(showExpertReview && expertReview)}
					ratingBreakdown={ratingBreakdown}
					isBreakdownOpen={isBreakdownOpen}
					onBreakdownOpenChange={handleBreakdownOpenChange}
					isDesktop={isDesktop}
				/>
			</div>
		</div>
	);

	if (isDesktop && hasBreakdown) {
		return (
			<Popover open={isBreakdownOpen} onOpenChange={handleBreakdownOpenChange}>
				<PopoverAnchor asChild>{content}</PopoverAnchor>
				<RatingBreakdownPopoverPanel
					averageRating={ratingClamped}
					totalCount={ratingCount}
					breakdown={ratingBreakdown}
					onClose={() => handleBreakdownOpenChange(false)}
					align="start"
				/>
			</Popover>
		);
	}

	return content;
}
